import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
  Image,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import CheckerPattern from '../components/CheckerPattern';
import RegistrationFormContent from '../components/RegistrationFormContent';
import { sendAdminNotificationEmail } from '../services/emailService';
import { submitHotspotClaimRequest } from '../services/database';
import { supabase, STORAGE_BUCKETS, getPublicUrl } from '../services/supabase';
import { uploadImageToStorage } from '../utils/uploadImageToStorage';

const RegisterScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(''); // store email for confirm screen
  const [confirmUsername, setConfirmUsername] = useState('');

  const pendingRegistrationData = useRef(null);

  const { signUp, confirmSignUp, createUserProfile, updateUserProfile, resendVerificationEmail, setRegistrationInProgress } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { showToast } = useToast();

  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    card: isDarkMode ? '#141414' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    border: isDarkMode ? '#2A2A2A' : '#E5E5E5',
    inputBg: isDarkMode ? '#1A1A1A' : '#F5F5F5',
    accent: '#94EB98',
    accentDark: '#7BC97F',
  };

  const doUpload = (bucket, asset, path) =>
    uploadImageToStorage(supabase, bucket, asset, path, (msg) => showToast(`Upload failed: ${msg}`, 'error'), getPublicUrl);

  const handleRegisterSubmit = async (data) => {
    if (data?.error) {
      setErrorMessage(data.error);
      return;
    }
    if (!data?.email?.trim() || !data?.password || !data?.username) {
      setErrorMessage('Please fill in required fields');
      return;
    }
    setErrorMessage('');
    setLoading(true);
    const trimmedEmail = data.email.trim();
    const result = await signUp(trimmedEmail, data.password, data.username);
    setLoading(false);
    if (result.success) {
      pendingRegistrationData.current = data;
      setConfirmEmail(trimmedEmail);
      setConfirmUsername(data.username);
      setShowConfirm(true);
    } else {
      setErrorMessage(result.error || 'Registration failed. Please try again.');
    }
  };

  const handleConfirm = async () => {
    if (!confirmCode) {
      setErrorMessage('Please enter the confirmation code');
      return;
    }
    setErrorMessage('');
    setLoading(true);

    // Prevent CompleteProfileScreen from flashing during the window between
    // session activation (inside confirmSignUp) and profile creation below.
    setRegistrationInProgress?.(true);

    try {
      const result = await confirmSignUp(confirmUsername, confirmCode);
      if (!result.success) {
        setLoading(false);
        let err = result.error || 'Confirmation failed';
        if (result.error?.includes('CodeMismatchException')) err = 'Invalid confirmation code. Please try again.';
        if (result.error?.includes('ExpiredCodeException')) err = 'Confirmation code has expired. Please request a new one.';
        setErrorMessage(err);
        return;
      }

      const data = pendingRegistrationData.current;
      if (!data) {
        setLoading(false);
        setErrorMessage('Session expired. Please sign up again.');
        return;
      }

      const email = confirmEmail || data.email?.trim();
      const isClub = data.accountType === 'CLUB';

      const commonProfileFields = {
        username: data.username,
        email,
        authProvider: 'email',
        rating: 1200,
        gamesPlayedInApp: 0,
        ...(data.city ? { city: data.city } : {}),
        ...(data.bio ? { bio: data.bio } : {}),
      };

      let profileData;
      if (!isClub) {
        profileData = {
          ...commonProfileFields,
          accountType: 'PLAYER',
          chesscomUsername: data.chesscomUsername || null,
          lichessUsername: data.lichessUsername || null,
          websiteUrl: data.websiteUrl || null,
          instagramUrl: data.instagramUrl || null,
          twitterUrl: data.twitterUrl || null,
          facebookUrl: data.facebookUrl || null,
          tiktokUrl: data.tiktokUrl || null,
        };
      } else {
        profileData = {
          ...commonProfileFields,
          accountType: 'CLUB',
          clubName: data.clubName || data.selectedClub?.name || data.username,
          address: data.clubAddress || data.selectedClub?.address || '',
          submissionPitch: data.submissionPitch || data.claimProofDescription || '',
          website: data.websiteUrl || null,
          instagramUrl: data.instagramUrl || null,
          twitterUrl: data.twitterUrl || null,
          facebookUrl: data.facebookUrl || null,
          tiktokUrl: data.tiktokUrl || null,
          approvalStatus: 'PENDING',
          submittedAt: new Date().toISOString(),
          lastSubmissionAttempt: new Date().toISOString(),
        };
      }

      const createResult = await createUserProfile(profileData);
      if (!createResult.success) {
        setLoading(false);
        setErrorMessage(createResult.error || 'Failed to create profile.');
        return;
      }
      const newUserId = createResult.user?.id;

      if (isClub) {
        let evidenceUrls = [];
        if (data.evidenceImages?.length > 0 && newUserId) {
          for (let i = 0; i < data.evidenceImages.length; i++) {
            const url = await doUpload(STORAGE_BUCKETS.CLUB_EVIDENCE, data.evidenceImages[i], `${newUserId}/evidence-${i}`);
            if (url) evidenceUrls.push(url);
          }
        }
        if (data.clubMode === 'CLAIM' && data.selectedClub) {
          try {
            await submitHotspotClaimRequest({
              hotspotId: data.selectedClub.id,
              requesterUserId: newUserId,
              email,
              name: data.username,
              proofDescription: data.claimProofDescription || '',
              contactInfo: data.claimContactInfo || null,
              evidenceUrls: evidenceUrls.length > 0 ? evidenceUrls : null,
            });
          } catch (e) {
            console.error('Claim request submission error:', e);
          }
        }
        const clubDisplayName = data.clubName || data.selectedClub?.name || data.username;
        const pitchText = data.clubMode === 'CLAIM'
          ? `CLAIM REQUEST: ${data.claimProofDescription || ''}`
          : data.submissionPitch || '';
        try {
          await sendAdminNotificationEmail({
            clubName: clubDisplayName,
            email,
            pitch: pitchText,
            address: data.clubAddress || data.selectedClub?.address || 'N/A',
            socialLinks: {
              website: data.websiteUrl || null,
              instagram: data.instagramUrl || null,
              twitter: data.twitterUrl || null,
              facebook: data.facebookUrl || null,
              tiktok: data.tiktokUrl || null,
            },
            isClaimRequest: data.clubMode === 'CLAIM',
            evidenceUrls,
            contactInfo: data.claimContactInfo || null,
          });
        } catch (e) {
          console.warn('Admin notification email failed:', e);
        }
        if (data.profileImage && (data.profileImage.uri || data.profileImage.base64 || data.profileImage.file || data.profileImage.arrayBuffer) && newUserId) {
          const picResult = await doUpload(STORAGE_BUCKETS.PROFILE_PICTURES, data.profileImage, `${newUserId}/profile`);
          const picPath = picResult?.path ?? picResult;
          if (picPath) {
            try {
              const picUp = await updateUserProfile({ profilePictureUrl: picPath }, { userId: newUserId });
              if (!picUp.success) throw new Error(picUp.error || 'Profile picture update failed');
            } catch (e) {
              console.error('Failed to save profile picture URL:', e);
            }
          }
        }
      } else {
        if (data.profileImage && (data.profileImage.uri || data.profileImage.base64 || data.profileImage.file || data.profileImage.arrayBuffer) && newUserId) {
          const picResult = await doUpload(STORAGE_BUCKETS.PROFILE_PICTURES, data.profileImage, `${newUserId}/profile`);
          const picPath = picResult?.path ?? picResult;
          if (picPath) {
            try {
              const picUp = await updateUserProfile({ profilePictureUrl: picPath }, { userId: newUserId });
              if (!picUp.success) throw new Error(picUp.error || 'Profile picture update failed');
            } catch (e) {
              console.error('Failed to save profile picture URL:', e);
            }
          }
        }
      }

      setLoading(false);
      showToast(isClub ? 'Account created! Your club application is under review.' : 'Profile setup complete! Welcome to ChessMaps', 'success');
      if (Platform.OS === 'web') {
        try { sessionStorage.setItem('chessmaps_profile_done', '1'); } catch (_) {}
        window.location.replace('/');
      } else {
        setTimeout(() => {
          try {
            navigation.reset({ index: 0, routes: [{ name: 'Home' }] });
          } catch (_) {}
        }, 500);
      }
    } finally {
      setRegistrationInProgress?.(false);
    }
  };

  // Confirmation code screen
  if (showConfirm) {
    return (
      <View style={[styles.container, { backgroundColor: theme.bg }]}>
        <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
        
        <CheckerPattern isDarkMode={isDarkMode} opacity={0.02} />
        
        <Pressable style={styles.themeToggle} onPress={toggleDarkMode}>
          <Text style={[styles.themeToggleText, { color: theme.textSecondary }]}>
            {isDarkMode ? '☀️' : '🌙'}
          </Text>
        </Pressable>

        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.keyboardView}>
          <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <View style={styles.brandSection}>
              <Image source={require('../../assets/chess-maps logo.png')} style={styles.logo} resizeMode="contain" />
              <Text style={[styles.brandName, { color: theme.text }]}>
                Chess<Text style={{ color: theme.accent }}>Maps</Text>
              </Text>
              <Text style={[styles.tagline, { color: theme.textSecondary }]}>Verify your email</Text>
            </View>

            <View style={styles.formSection}>
              <Text style={[styles.confirmText, { color: theme.textSecondary }]}>
                We've sent a verification code to{' '}
                <Text style={{ color: theme.text, fontWeight: '600' }}>{confirmEmail}</Text>
              </Text>

              {errorMessage ? (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{errorMessage}</Text>
                </View>
              ) : null}

              <View style={styles.inputGroup}>
                <TextInput
                  style={[styles.input, { color: theme.text, borderBottomColor: theme.border }]}
                  placeholder="Enter 6-digit code"
                  placeholderTextColor={theme.textSecondary}
                  value={confirmCode}
                  onChangeText={(text) => {
                    setConfirmCode(text);
                    setErrorMessage('');
                  }}
                  keyboardType="number-pad"
                  autoCapitalize="none"
                  maxLength={6}
                />
              </View>

              <Pressable
                style={({ pressed }) => [styles.primaryButton, { opacity: pressed ? 0.9 : (loading ? 0.7 : 1) }]}
                onPress={handleConfirm}
                disabled={loading}
              >
                {loading ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Text style={styles.primaryButtonText}>Verify Email</Text>}
              </Pressable>

              <Pressable
                onPress={async () => {
                  setResendingEmail(true);
                  setErrorMessage('');
                  const result = await resendVerificationEmail();
                  setResendingEmail(false);
                  if (result.success) {
                    setErrorMessage('');
                    Alert.alert('Success', 'Verification email sent! Please check your inbox.');
                  } else {
                    setErrorMessage(result.error || 'Failed to resend verification email');
                  }
                }}
                disabled={resendingEmail}
                style={styles.resendLink}
              >
                <Text style={[styles.signupLink, { color: theme.accent, opacity: resendingEmail ? 0.5 : 1 }]}>
                  {resendingEmail ? 'Sending...' : "Didn't receive code? Resend"}
                </Text>
              </Pressable>

              <Pressable onPress={() => navigation.navigate('Login')} style={styles.backLink}>
                <Text style={[styles.signupLink, { color: theme.accent }]}>Back to Login</Text>
              </Pressable>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.bg }]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />

      <CheckerPattern isDarkMode={isDarkMode} opacity={0.008} />

      <Pressable style={styles.themeToggle} onPress={toggleDarkMode}>
        <Text style={[styles.themeToggleText, { color: theme.textSecondary }]}>
          {isDarkMode ? '☀️' : '🌙'}
        </Text>
      </Pressable>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.keyboardView}>
        <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
          
          {/* Logo & Branding */}
          <View style={styles.brandSection}>
            <Image source={require('../../assets/chess-maps logo.png')} style={styles.logoSmall} resizeMode="contain" />
            <Text style={[styles.brandName, { color: theme.text }]}>
              Chess<Text style={{ color: theme.accent }}>Maps</Text>
            </Text>
            <Text style={[styles.tagline, { color: theme.textSecondary }]}>
              Create your account
            </Text>
          </View>

          <View style={styles.formSection}>
            <RegistrationFormContent
              isOAuth={false}
              suggestedUsername=""
              displayEmail=""
              onSubmit={handleRegisterSubmit}
              loading={loading}
              errorMessage={errorMessage}
              onClearError={() => setErrorMessage('')}
              theme={theme}
              showEmailPassword={true}
              showSocialNote={true}
              submitLabel="Create Account"
              headerTagline="Choose Player or Club, then fill in the details below."
            />

            {/* Login Link */}
            <View style={styles.signupSection}>
              <Text style={[styles.signupText, { color: theme.textSecondary }]}>Already have an account?</Text>
              <Pressable onPress={() => navigation.navigate('Login')}>
                <Text style={[styles.signupLink, { color: theme.accent }]}>Log In</Text>
              </Pressable>
            </View>

            {/* View Pricing Link */}
            <View style={styles.pricingSection}>
              <Pressable
                onPress={() => navigation.navigate('Pricing')}
                accessibilityRole="button"
                accessibilityLabel="ChessMaps Pro"
                style={({ pressed }) => [
                  styles.pricingButton,
                  { 
                    borderColor: theme.border,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Text style={[styles.pricingButtonText, { color: theme.accent }]}>
                  ⭐ View Pricing Plans
                </Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  themeToggle: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 10,
  },
  themeToggleText: {
    fontSize: 24,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 60,
  },
  brandSection: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 16,
  },
  logoSmall: {
    width: 72,
    height: 72,
    marginBottom: 12,
  },
  brandName: {
    fontSize: 28,
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  tagline: {
    fontSize: 15,
    marginTop: 6,
    fontWeight: '400',
  },
  confirmText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  formSection: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  socialNote: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    marginBottom: 20,
  },
  socialNoteText: {
    fontSize: 14,
    textAlign: 'center',
  },
  typeSelector: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  typeButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
  },
  typeButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  inputGroup: {
    gap: 20,
    marginBottom: 24,
  },
  input: {
    paddingHorizontal: 0,
    paddingVertical: 14,
    fontSize: 17,
    borderWidth: 0,
    borderBottomWidth: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { outline: 'none' }),
  },
  errorContainer: {
    backgroundColor: '#FFE5E5',
    borderLeftWidth: 4,
    borderLeftColor: '#FF4444',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: '#CC0000',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  primaryButton: {
    backgroundColor: '#84D288',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    elevation: 8,
    shadowColor: '#76AF87',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  signupSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
  },
  signupText: {
    fontSize: 14,
  },
  signupLink: {
    fontSize: 14,
    fontWeight: '600',
  },
  pricingSection: {
    marginTop: 20,
    alignItems: 'center',
  },
  pricingButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 1.5,
    borderStyle: 'dashed',
  },
  pricingButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  backLink: {
    alignItems: 'center',
    paddingVertical: 10,
  },
  resendLink: {
    alignItems: 'center',
    paddingVertical: 10,
    marginTop: 10,
  },
  textArea: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    borderWidth: 0,
    borderRadius: 10,
    minHeight: 100,
    textAlignVertical: 'top',
    ...(Platform.OS === 'web' && { outline: 'none' }),
  },
  charCount: {
    fontSize: 12,
    textAlign: 'right',
    marginTop: 4,
  },
  socialLinksSection: {
    marginTop: 8,
    paddingTop: 16,
    borderTopWidth: 1,
    borderStyle: 'dashed',
  },
  socialLinksTitle: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 12,
  },
  approvalNotice: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderLeftWidth: 4,
    marginTop: 8,
  },
  approvalNoticeText: {
    fontSize: 13,
    lineHeight: 18,
  },
  // Club Mode Toggle styles
  clubModeContainer: {
    marginBottom: 16,
  },
  clubModeLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  clubModeToggle: {
    flexDirection: 'row',
    gap: 10,
  },
  clubModeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  clubModeButtonText: {
    fontSize: 13,
    fontWeight: '500',
  },
  // Selected Club Card styles
  selectedClubCard: {
    padding: 14,
    borderRadius: 12,
    borderWidth: 2,
    marginBottom: 16,
  },
  selectedClubHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  selectedClubInfo: {
    flex: 1,
  },
  selectedClubName: {
    fontSize: 16,
    fontWeight: '600',
  },
  selectedClubAddress: {
    fontSize: 13,
    marginTop: 2,
  },
  clearClubButton: {
    padding: 4,
  },
  // Search Club Button styles
  searchClubButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    marginBottom: 16,
    gap: 10,
  },
  searchClubButtonText: {
    fontSize: 15,
    flex: 1,
  },
  // Claim Info Box styles
  claimInfoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 10,
    marginTop: 8,
    gap: 10,
  },
  claimInfoText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  // Modal styles - match other app modals (centered, not bottom sheet)
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    ...(Platform.OS === 'web' && {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
    }),
  },
  modalContent: {
    borderRadius: 16,
    maxHeight: '80%',
    width: '100%',
    maxWidth: 450,
    paddingBottom: Platform.OS === 'ios' ? 34 : 20,
    ...(Platform.OS === 'web' && {
      outline: 'none',
      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  modalCloseButton: {
    padding: 4,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 0,
    gap: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
    ...(Platform.OS === 'web' && { outline: 'none' }),
  },
  claimFilterToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 12,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  claimFilterToggleText: {
    flex: 1,
    fontSize: 14,
  },
  claimFiltersPanel: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderRadius: 10,
    borderWidth: 1,
  },
  claimFilterLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  claimFilterInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    fontSize: 14,
  },
  claimFilterChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  claimFilterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
  },
  claimFilterChipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  searchLoadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 12,
  },
  searchLoadingText: {
    fontSize: 14,
  },
  searchEmptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 12,
  },
  searchEmptyText: {
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  searchEmptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  searchResultsList: {
    maxHeight: 350,
  },
  searchResultsHeader: {
    fontSize: 13,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  clubResultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 10,
    borderWidth: 1,
    gap: 12,
  },
  clubResultIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(148, 235, 152, 0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clubResultInfo: {
    flex: 1,
  },
  clubResultNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  clubResultName: {
    fontSize: 15,
    fontWeight: '600',
  },
  clubResultCategory: {
    fontSize: 11,
    textTransform: 'uppercase',
    opacity: 0.8,
  },
  clubResultAddress: {
    fontSize: 13,
    marginTop: 2,
  },
  modalFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  modalFooterText: {
    fontSize: 13,
    textAlign: 'center',
  },
});

export default RegisterScreen;
